import { useCallback, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  ContractSmsPreviewQuery,
  useContractSmsPreviewQuery,
  useSendContractSmsTextMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useDebouncedValue } from '../Meta/useDebouncedValue';
import { useToasts } from '../Toast/useToasts';
import { settleOutreachCache } from './useContractOutreach';
import { IEsignFieldError, toEsignErrors } from './useEsignMutations';
import { useEsignPermissions } from './useEsignPermissions';

/**
 * ТЗ 0078 — SMS подписанту со своим текстом из карточки договора.
 *
 * Предпросмотр — серверный: только сервер знает кодировку, хвост отписки
 * («первое SMS человеку») и длину подставленных значений. Текст уходит с
 * задержкой 400 мс, иначе каждая буква была бы запросом.
 */

export type IContractSmsPreview = NonNullable<
  ContractSmsPreviewQuery['previewContractSms']
>;

/** Задержка предпросмотра: печать не должна быть очередью запросов. */
export const SMS_PREVIEW_DEBOUNCE_MS = 400;

export interface IUseContractSmsPreview {
  preview: IContractSmsPreview | null;
  /** Предпросмотр отстал от набранного текста (задержка или запрос в пути). */
  pending: boolean;
  failed: boolean;
  /** Спросить сервер ещё раз: «не удалось посчитать» — не тупик. */
  reload: () => void;
}

/**
 * Предпросмотр SMS. Пустой текст — запроса нет. Пока ответ на новый текст
 * не пришёл, держим прошлый (`previousData`): счётчик сегментов не мигает
 * прочерком на каждое нажатие.
 */
export const useContractSmsPreview = (
  contractId: string,
  text: string,
  enabled: boolean,
): IUseContractSmsPreview => {
  const { canWriteStudent } = useEsignPermissions();
  const trimmed = text.trim();
  const debounced = useDebouncedValue(trimmed, SMS_PREVIEW_DEBOUNCE_MS);
  const skip = !enabled || !canWriteStudent || !contractId || !debounced;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useContractSmsPreviewQuery({
    variables: { contractId, text: debounced },
    skip,
    // Хвост отписки зависит от того, уходило ли уже SMS: после отправки тот
    // же текст обязан пересчитаться, а не взяться из кэша.
    fetchPolicy: 'network-only',
  });
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  if (!trimmed) {
    return { preview: null, pending: false, failed: false, reload };
  }
  const current = (data ?? previousData)?.previewContractSms ?? null;
  return {
    preview: current,
    pending: debounced !== trimmed || loading,
    failed: Boolean(error) && !current,
    reload,
  };
};

export interface IContractSmsResult {
  ok: boolean;
  errors: IEsignFieldError[];
}

/**
 * Отправить SMS со своим текстом. Ответ — полное состояние напоминаний: оно
 * пишется в запрос секции «Напоминания SMS» тем же приёмом, что у «Напомнить
 * сейчас», и новое сообщение сразу встаёт в лестницу.
 *
 * Бизнес-отказ (`COOLDOWN`, `OPTED_OUT`, `UNKNOWN_PLACEHOLDER`…) рисует
 * форма под полем; отказ прав или сети — тостом.
 */
export const useContractSmsSend = (contractId: string) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [sendSms, { loading: sending }] = useSendContractSmsTextMutation();
  // Защёлка в ref: второй клик в том же такте ещё видит `loading=false` и
  // поставил бы второе SMS в очередь.
  const inFlightRef = useRef(false);

  const send = useCallback(
    async (
      text: string,
      templateId: string | null,
    ): Promise<IContractSmsResult> => {
      if (inFlightRef.current) return { ok: false, errors: [] };
      inFlightRef.current = true;
      try {
        const { data } = await sendSms({
          variables: { contractId, text: text.trim(), templateId },
          update: (cache, { data: answer }) =>
            settleOutreachCache(cache, contractId, answer?.sendContractSms),
        });
        const payload = data?.sendContractSms;
        const errors = toEsignErrors(payload?.customErrors);
        if (payload?.success) {
          addToast({
            data: {
              type: ToastTypes.success,
              title: t('esign.sms.toast.sent'),
            },
            type: GlobalToastTypeNames.Esign,
          });
          return { ok: true, errors };
        }
        return { ok: false, errors };
      } catch (caught) {
        const noAccess =
          caught instanceof ApolloError && caught.graphQLErrors.length > 0;
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('esign.sms.toast.failed'),
            text: t(
              noAccess
                ? 'esign.sms.errors.NO_ACCESS'
                : 'esign.sms.errors.NETWORK',
            ),
          },
          type: GlobalToastTypeNames.Esign,
        });
        return { ok: false, errors: [] };
      } finally {
        inFlightRef.current = false;
      }
    },
    [addToast, contractId, sendSms, t],
  );

  return { send, sending };
};
